// Compile a Dal Segno Machine score-program into the app's render model — a
// REAL engraved piece of sheet music (staves, riff noteheads, repeat
// barlines, boxed rehearsal letters, segno/coda glyphs, Italian directives)
// whose performance IS the computation. The engraving never changes with the
// input: the sheet is the program, the soloist's choice is stdin. Pure and
// React-free; the exhibit engraves the result with ScoreView.

import type { Bar, MelodyNote, Song } from './song';
import { countLabel, jumpLabel, type SegnoLine, type SegnoProgram } from './segnoMachine';

/** Each register letter keeps one recognisable written figure. */
const FIGURES: Record<string, { midi: number; beat: number; duration: number }[]> = {
  // The vamp: root, fifth, octave — quarter, quarter, half.
  A: [
    { midi: 48, beat: 1, duration: 1 },
    { midi: 55, beat: 2, duration: 1 },
    { midi: 60, beat: 3, duration: 2 },
  ],
  // The answer, up high — two halves.
  B: [
    { midi: 64, beat: 1, duration: 2 },
    { midi: 67, beat: 3, duration: 2 },
  ],
  // The accumulator — rising quarters into a half.
  C: [
    { midi: 52, beat: 1, duration: 1 },
    { midi: 59, beat: 2, duration: 1 },
    { midi: 62, beat: 3, duration: 2 },
  ],
};

function figureFor(letter: string, barIndex: number): MelodyNote[] {
  const fig = FIGURES[letter] ?? [{ midi: 60, beat: 1, duration: 4 }];
  return fig.map((n, i) => ({ id: `sm-${barIndex}-${i}`, ...n }));
}

function wholeRest(barIndex: number): MelodyNote[] {
  return [{ id: `sm-${barIndex}-r`, midi: 60, beat: 1, duration: 4, rest: true }];
}

function lineDirective(line: SegnoLine, lines: SegnoLine[]): string | undefined {
  const jumps = (line.jumps ?? []).map((j) => jumpLabel(j, lines)).join(' · ');
  return jumps.length ? jumps : undefined;
}

/**
 * One program line = one written bar. Play lines wear their repeat brackets
 * and their count expression as staff text ("×(A−1)" where a chart would
 * print "3x"); jump directions print right-aligned over the bar's end like
 * any D.S.; guard-only lines are segno-marked rest bars; Fine wears the coda.
 */
export function segnoProgramToSong(program: SegnoProgram, title: string): Song {
  const lines = program.lines;
  const bars: Bar[] = lines.map((line, i) => {
    const directive = lineDirective(line, lines);
    if (line.kind === 'mark') {
      return {
        id: `line-${i}`,
        beats: 4,
        chords: [],
        melody: wholeRest(i),
        segno: !line.fine,
        coda: !!line.fine,
        directive: line.fine ? 'Fine' : directive,
        closeBarline: line.fine ? ('final' as const) : undefined,
      };
    }
    return {
      id: `line-${i}`,
      beats: 4,
      chords: [],
      melody: figureFor(line.letter, i),
      openBarline: 'repeat' as const,
      closeBarline: 'repeat' as const,
      staffText: countLabel(line.expr),
      staffTextAbove: true,
      directive,
    };
  });

  // Every play line is its own section named by its register letter, so the
  // engraver draws the boxed rehearsal letter on each instruction; guard
  // lines ride in the current section ('Main' never draws, covering a
  // leading mark).
  const sections: Song['sections'] = [];
  bars.forEach((bar, i) => {
    const line = lines[i];
    if (line.kind === 'play' || sections.length === 0) {
      sections.push({
        id: `sec-${i}`,
        name: line.kind === 'play' ? line.letter : 'Main',
        bars: [bar],
      });
    } else {
      sections[sections.length - 1].bars.push(bar);
    }
  });

  return {
    id: `segno-${title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`,
    title,
    key: 'C',
    timeSignature: [4, 4],
    sections,
  };
}
